import type { ComponentChildren } from 'preact'
import { useState } from 'preact/hooks'
import { AlertCircle, Check, Clock, LayoutTemplate, LifeBuoy, Type } from 'lucide-preact'
import type { GenerationFailureView } from '@/utils/generationFailure'

export const FAILURE_PRIMARY_BUTTON =
    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500'
export const FAILURE_SECONDARY_BUTTON =
    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500'

// Amber means the person can fix it, slate that a retry usually works, red that it is broken on our side.
const DISC_CLASS: Record<GenerationFailureView['kind'], string> = {
    person: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    retry: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    ours: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    unknown: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    ready: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
}

function iconFor(view: GenerationFailureView) {
    if (view.kind === 'ready') return Check
    if (view.code === 'FontUnavailable') return Type
    if (view.code === 'TemplateMissingBodySlot' || view.code === 'EmailTemplateNotBound') return LayoutTemplate
    if (view.kind === 'retry') return Clock
    if (view.kind === 'ours') return LifeBuoy
    return AlertCircle
}

interface GenerationFailurePanelProps {
    view: GenerationFailureView
    /**
     * `panel` fills an empty canvas; `banner` is a strip above a page that already exists, so a failed
     * rebuild explains itself without hiding the page the person was working on.
     */
    variant?: 'panel' | 'banner'
    /** The buttons that fix it. Omitted for a person who cannot act on it. */
    actions?: ComponentChildren
    /** Context under the actions, such as where a brand setting lives. */
    footnote?: ComponentChildren
    /** Shown instead of actions to someone who can view the page but not change it. */
    viewerNote?: string | null
    /** The banner's opening sentence about what is shown below it; null for none. */
    lead?: string | null
}

export function GenerationFailurePanel({
    view, variant = 'panel', actions, footnote, viewerNote, lead = 'The page below is the last version that built.',
}: GenerationFailurePanelProps) {
    const Icon = iconFor(view)
    if (variant === 'banner') {
        return (
            <div role="alert" className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shrink-0">
                <div className="flex items-start gap-2.5 min-w-0 flex-1 basis-80">
                    <span className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${DISC_CLASS[view.kind]}`}>
                        <Icon className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{view.title}</p>
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                            {lead && `${lead} `}{view.message}
                        </p>
                        {viewerNote && <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{viewerNote}</p>}
                        {footnote && <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500 dark:text-slate-400">{footnote}</div>}
                    </div>
                </div>
                {view.reference && <span className="text-xs text-slate-500 dark:text-slate-400"><ReferenceChip reference={view.reference} /></span>}
                {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
            </div>
        )
    }
    return (
        <div role="alert" className="w-full max-w-md grid gap-4 text-left">
            <div className="flex items-start gap-3.5">
                <span className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${DISC_CLASS[view.kind]}`}>
                    <Icon className="w-[18px] h-[18px]" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                    <p className="text-base font-semibold text-slate-900 dark:text-slate-100 text-balance">{view.title}</p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{view.message}</p>
                </div>
            </div>
            {actions && <div className="flex flex-wrap gap-2 pl-[50px]">{actions}</div>}
            {(footnote || view.reference) && (
                <div className="pl-[50px] grid gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                    {footnote}
                    {view.reference && <ReferenceChip reference={view.reference} />}
                </div>
            )}
            {viewerNote && (
                <p className="ml-[50px] pt-3 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300">{viewerNote}</p>
            )}
        </div>
    )
}

function ReferenceChip({ reference }: { reference: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500) }
        try {
            navigator.clipboard.writeText(reference).then(done, () => {})
        } catch {
            // No clipboard here: the reference stays on screen to read out.
        }
    }
    return (
        <span className="inline-flex w-fit items-center gap-2">
            <span>Reference for support</span>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 pl-2.5 pr-1 py-0.5 font-mono tabular-nums text-slate-700 dark:text-slate-200 select-all">
                {reference}
                <button
                    type="button"
                    onClick={copy}
                    className="font-sans rounded px-1.5 py-0.5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </span>
        </span>
    )
}
